import { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { Toast, ToastType } from '../components/Toast/types';

interface ShowToastOptions {
  duration?: number;
  type?: ToastType;
}

interface ToastContextType {
  toasts: Toast[];
  showToast: (message: string, typeOrOptions?: ToastType | ShowToastOptions) => void;
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
  showLoading: (message: string) => string;
  removeToast: (id: string) => void;
  clearToasts: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
  defaultDuration?: number;
  maxToasts?: number;
}

export function ToastProvider({
  children,
  defaultDuration = 5000,
  maxToasts = 5
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const clearToastTimeout = useCallback((id: string) => {
    const timeout = timeoutsRef.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutsRef.current.delete(id);
    }
  }, []);

  const removeToast = useCallback((id: string) => {
    clearToastTimeout(id);
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, [clearToastTimeout]);

  const clearToasts = useCallback(() => {
    timeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    timeoutsRef.current.clear();
    setToasts([]);
  }, []);

  const showToast = useCallback((
    message: string,
    typeOrOptions: ToastType | ShowToastOptions = 'error'
  ) => {
    const options: ShowToastOptions = typeof typeOrOptions === 'string'
      ? { type: typeOrOptions }
      : typeOrOptions;

    const id = `toast-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    const type = options.type || 'error';
    const rawDuration = options.duration;
    const duration = (typeof rawDuration === 'number' && isFinite(rawDuration))
      ? rawDuration
      : (type === 'loading' ? 0 : defaultDuration);

    const newToast: Toast = { id, message, type, duration };

    setToasts(prev => {
      const updated = [...prev, newToast];
      return updated.length > maxToasts ? updated.slice(-maxToasts) : updated;
    });

    if (duration > 0) {
      const timeout = setTimeout(() => {
        removeToast(id);
      }, duration);
      timeoutsRef.current.set(id, timeout);
    }

    return id;
  }, [defaultDuration, maxToasts, removeToast]);

  const showSuccess = useCallback((message: string, duration?: number) => {
    return showToast(message, { type: 'success', duration });
  }, [showToast]);

  const showError = useCallback((message: string, duration?: number) => {
    return showToast(message, { type: 'error', duration });
  }, [showToast]);

  const showWarning = useCallback((message: string, duration?: number) => {
    return showToast(message, { type: 'warning', duration });
  }, [showToast]);

  const showInfo = useCallback((message: string, duration?: number) => {
    return showToast(message, { type: 'info', duration });
  }, [showToast]);

  const showLoading = useCallback((message: string) => {
    return showToast(message, { type: 'loading', duration: 0 });
  }, [showToast]);

  const value: ToastContextType = useMemo(() => ({
    toasts,
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showLoading,
    removeToast,
    clearToasts,
  }), [toasts, showToast, showSuccess, showError, showWarning, showInfo, showLoading, removeToast, clearToasts]);

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useToast(): ToastContextType {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
